import { useEffect, useState } from "react";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Payment";

function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/GetPaymentHistory`, {
        headers: {
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log("📦 GetPaymentHistory response:", data);
      const list = Array.isArray(data)
        ? data
        : (data.payments ?? data.data ?? data.history ?? []);
      setPayments(list);
    } catch (err) {
      console.error("❌ Error fetching payments:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatAmount = (amount, currency = "INR") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency?.toUpperCase() ?? "INR",
      minimumFractionDigits: 2,
    }).format(amount / 100); // Razorpay sends in paise
  };

  const statusStyle = (status) => {
    const map = {
      captured: {
        cls: "bg-success-subtle text-success border-success-subtle",
        icon: "bi-check-circle-fill",
      },
      paid: {
        cls: "bg-success-subtle text-success border-success-subtle",
        icon: "bi-check-circle-fill",
      },
      success: {
        cls: "bg-success-subtle text-success border-success-subtle",
        icon: "bi-check-circle-fill",
      },
      failed: {
        cls: "bg-danger-subtle text-danger border-danger-subtle",
        icon: "bi-x-circle-fill",
      },
      failure: {
        cls: "bg-danger-subtle text-danger border-danger-subtle",
        icon: "bi-x-circle-fill",
      },
      pending: {
        cls: "bg-warning-subtle text-warning border-warning-subtle",
        icon: "bi-clock-fill",
      },
      created: {
        cls: "bg-warning-subtle text-warning border-warning-subtle",
        icon: "bi-clock-fill",
      },
      refunded: {
        cls: "bg-info-subtle text-info border-info-subtle",
        icon: "bi-arrow-counterclockwise",
      },
    };
    return (
      map[status?.toLowerCase()] ?? {
        cls: "bg-light text-dark border-secondary-subtle",
        icon: "bi-circle",
      }
    );
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredPayments = payments.filter((p) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const paymentId =
      p.paymentId ?? p.payment_id ?? p.razorpay_payment_id ?? "";
    const orderId = p.orderId ?? p.order_id ?? p.razorpay_order_id ?? "";
    const courseTitle = p.courseTitle ?? p.course?.title ?? "";
    const courseId = p.courseId ?? p.course_id ?? "";
    const userName = p.userName ?? p.user?.name ?? p.name ?? "";
    const userEmail = p.userEmail ?? p.user?.email ?? p.email ?? "";
    const userPhone = p.userPhone ?? p.user?.phone ?? p.phone ?? "";
    const userId = p.userId ?? p.user?.userId ?? "";
    const status = p.status ?? "";
    const method = p.method ?? p.payment_method ?? "";
    const couponCode = p.couponCode ?? p.coupon_code ?? "";
    return (
      String(paymentId).toLowerCase().includes(q) ||
      String(orderId).toLowerCase().includes(q) ||
      String(courseTitle).toLowerCase().includes(q) ||
      String(courseId).toLowerCase().includes(q) ||
      String(userName).toLowerCase().includes(q) ||
      String(userEmail).toLowerCase().includes(q) ||
      String(userPhone).toLowerCase().includes(q) ||
      String(userId).toLowerCase().includes(q) ||
      String(status).toLowerCase().includes(q) ||
      String(method).toLowerCase().includes(q) ||
      String(couponCode).toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedPayments = filteredPayments.slice(
    startIdx,
    startIdx + pageSize,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    Payment History{" "}
                    {!loading && !error && (
                      <span className="text-secondary">
                        ({payments.length})
                      </span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    All Transactions / Orders / Revenue
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching payment history…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load payments.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchPayments}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by payment ID, order ID, user, email, phone, course, status, method..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredPayments.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredPayments.length)} of{" "}
                {filteredPayments.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="py-2" style={{ width: "5%" }}>
                      No.
                    </th>
                    <th className="lh-1 py-2" style={{ width: "50%" }}>
                      Transaction & User Details
                      <i className="bi bi-receipt ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Order / Payer / Payment Reference
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Amount &amp; Status
                      <i className="bi bi-currency-rupee ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Value &amp; Payment Status
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Date &amp; Time
                      <i className="bi bi-calendar-event ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Transaction Timestamp
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center py-5 text-secondary lh-md"
                      >
                        <i className="bi bi-credit-card fs-1 d-block"></i>
                        {payments.length === 0
                          ? "No payment records found."
                          : "No payments match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedPayments.map((payment, idx) => {
                      // ── field normalisation — adjust after seeing real response ──
                      const id = payment._id ?? payment.id;
                      const orderId =
                        payment.orderId ??
                        payment.order_id ??
                        payment.razorpay_order_id ??
                        null;
                      const paymentId =
                        payment.paymentId ??
                        payment.payment_id ??
                        payment.razorpay_payment_id ??
                        null;
                      const amount = payment.amount ?? null;
                      const currency = payment.currency ?? "INR";
                      const status = payment.status ?? null;
                      const method =
                        payment.method ?? payment.payment_method ?? null;
                      const courseId =
                        payment.courseId ?? payment.course_id ?? null;
                      const courseTitle =
                        payment.courseTitle ?? payment.course?.title ?? null;
                      const couponCode =
                        payment.couponCode ?? payment.coupon_code ?? null;
                      const discount =
                        payment.discount ?? payment.discount_amount ?? null;

                      // user
                      const userName =
                        payment.userName ??
                        payment.user?.name ??
                        payment.name ??
                        null;
                      const userEmail =
                        payment.userEmail ??
                        payment.user?.email ??
                        payment.email ??
                        null;
                      const userPhone =
                        payment.userPhone ??
                        payment.user?.phone ??
                        payment.phone ??
                        null;
                      const userId =
                        payment.userId ?? payment.user?.userId ?? null;
                      const userPic =
                        payment.profilePic ?? payment.user?.profilePic ?? null;

                      // timestamps
                      const createdAt =
                        payment.createdAt ?? payment.created_at ?? null;

                      const { cls: statusCls, icon: statusIcon } =
                        statusStyle(status);

                      return (
                        <tr key={id ?? idx}>
                          <td>{startIdx + idx + 1}.</td>

                          {/* ── Transaction Details ── */}
                          <td className="lh-sm py-4">
                            {paymentId && (
                              <>
                                <span className="text-dark">
                                  <strong>Payment ID:</strong>
                                </span>{" "}
                                <code className="text-secondary small">
                                  {paymentId}
                                </code>
                                <br />
                              </>
                            )}
                            {orderId && (
                              <>
                                <span className="text-dark">
                                  <strong>Order ID:</strong>
                                </span>{" "}
                                <code className="text-secondary small">
                                  {orderId}
                                </code>
                                <br />
                              </>
                            )}
                            {courseTitle && (
                              <>
                                <span className="text-dark">
                                  <strong>Course:</strong>
                                </span>{" "}
                                {courseTitle}
                                <br />
                              </>
                            )}
                            {courseId && !courseTitle && (
                              <>
                                <span className="text-dark">
                                  <strong>Course ID:</strong>
                                </span>{" "}
                                <code className="text-secondary small">
                                  {courseId}
                                </code>
                                <br />
                              </>
                            )}
                            {method && (
                              <>
                                <span className="text-dark">
                                  <strong>Method:</strong>
                                </span>{" "}
                                <span className="badge bg-light border text-dark small px-2 rounded-3">
                                  <i className="bi bi-credit-card me-1"></i>
                                  {method}
                                </span>
                                <br />
                              </>
                            )}
                            {couponCode && (
                              <>
                                <span className="text-dark">
                                  <strong>Coupon:</strong>
                                </span>{" "}
                                <span className="badge bg-dark text-white px-2 py-1 rounded-3 small">
                                  {couponCode}
                                </span>
                                {discount != null && (
                                  <span className="text-success ms-1 small">
                                    −₹{discount}
                                  </span>
                                )}
                                <br />
                              </>
                            )}

                            <div className="d-flex align-items-start gap-3">
                              {/* Avatar */}
                              {userPic ? (
                                <img
                                  src={userPic}
                                  alt={userName}
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    border: "2px solid #dee2e6",
                                  }}
                                  onError={(e) =>
                                    (e.target.style.display = "none")
                                  }
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    background: "#e9ecef",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: "#6c757d",
                                    flexShrink: 0,
                                    border: "2px solid #dee2e6",
                                  }}
                                >
                                  {getInitials(userName)}
                                </div>
                              )}
                              <div>
                                {userName && (
                                  <>
                                    <span className="fw-semibold text-dark">
                                      {userName}
                                    </span>
                                    <br />
                                  </>
                                )}
                                {userId && (
                                  <>
                                    <span className="text-dark">
                                      <strong>User ID:</strong>
                                    </span>{" "}
                                    <code className="text-secondary small">
                                      {userId}
                                    </code>
                                    <br />
                                  </>
                                )}
                                {userEmail && (
                                  <>
                                    <span className="text-dark">
                                      <strong>Email:</strong>
                                    </span>{" "}
                                    {userEmail}
                                    <br />
                                  </>
                                )}
                                {userPhone && (
                                  <>
                                    <span className="text-dark">
                                      <strong>Phone:</strong>
                                    </span>{" "}
                                    {userPhone}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* ── Amount & Status ── */}
                          <td className="lh-sm py-4">
                            <div className="fw-semibold fs-5 text-dark mb-1">
                              {formatAmount(amount, currency)}
                            </div>
                            {status && (
                              <span
                                className={`badge border px-2 py-1 rounded-3 d-inline-flex align-items-center gap-1 ${statusCls}`}
                              >
                                <i className={`bi ${statusIcon}`}></i>
                                {status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                              </span>
                            )}
                          </td>

                          {/* ── Date & Time ── */}
                          <td className="lh-sm py-4 text-secondary small">
                            {formatDate(createdAt)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && filteredPayments.length > 0 && totalPages > 1 && (
            <nav aria-label="Payments pagination" className="mt-3">
              <ul className="pagination justify-content-center mb-0 flex-wrap">
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {getPageNumbers().map((num) => (
                  <li
                    key={num}
                    className={`page-item ${num === safePage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default PaymentHistory;
